export interface IAppsFlyerService {
  grantConsent: () => void
  revokeConsent: () => void
}

const TARGETING = 'C0004' // CookieCategory.TARGETING

// AF() only while C0004 is granted and measurement is enabled. Calls made
// before that (consent pending until geo, the SDK not loaded or enabled yet)
// wait here (at most 20), run in order once measurement is enabled, and are
// dropped by a denial.
const MAX_PENDING_CALLS = 20
let pendingCalls: unknown[][] = []
let measuring = false

export const callAppsFlyer = (...args: unknown[]): void => {
  const consent = window.bfConsent?.get()
  if (!consent) return
  const pending = consent.status === 'pending'
  if (!pending && !consent.groups.includes(TARGETING)) return
  if (pending || !measuring) {
    if (pendingCalls.length < MAX_PENDING_CALLS) pendingCalls.push(args)
    return
  }
  if (typeof AF === 'function') AF(...args)
}

// The SDK may only start loading at the grant (window.bfLoadAppsFlyer, index.html):
// checked every 100 ms, reported once after 5 s, then checked every second until
// it is ready or its script failed to load.
const READY_POLL_MS = 100
const READY_REPORT_TRIES = 50
const READY_SLOW_POLL_MS = 1000

export const createAppsFlyerService = (): IAppsFlyerService | null => {
  if (__DEV__) {
    return null
  }
  // Documentation explaining controlling AppsFlyer: https://support.appsflyer.com/hc/en-us/articles/360001610038-PBA-Web-SDK-integration-guide#explicit-control

  // The latest consent wins: until the SDK is ready, a poll applies whatever
  // grantConsent/revokeConsent asked for last, then stops.
  let granted = false
  let poll: ReturnType<typeof setTimeout> | null = null
  let reported = false

  const apply = (): boolean => {
    const pba = window.AF_SDK?.PLUGINS?.PBA
    const set = granted ? pba?.enableMeasurement : pba?.disableMeasurement
    if (!set) return false
    set()
    measuring = granted
    if (granted) {
      const queued = pendingCalls
      pendingCalls = []
      if (typeof AF === 'function') for (const args of queued) AF(...args)
    }
    return true
  }

  const report = (tryCount: number) => {
    if (reported) return
    reported = true
    logEvent('web_appsflyer_service_not_initialized', { tryCount, functionName: granted ? 'grantConsent' : 'revokeConsent' })
  }

  const stopPoll = () => {
    if (poll) clearTimeout(poll)
    poll = null
  }

  const reconcile = () => {
    if (apply()) {
      stopPoll()
      return
    }
    if (poll) return
    let tryCount = 0
    const check = () => {
      poll = null
      tryCount++
      if (apply()) return
      if (window.bfLoadAppsFlyer?.failed === true) {
        report(tryCount)
        return
      }
      if (tryCount === READY_REPORT_TRIES) report(tryCount)
      poll = setTimeout(check, tryCount < READY_REPORT_TRIES ? READY_POLL_MS : READY_SLOW_POLL_MS)
    }
    poll = setTimeout(check, READY_POLL_MS)
  }

  return {
    grantConsent: () => {
      granted = true
      window.bfLoadAppsFlyer?.()
      reconcile()
    },
    revokeConsent: () => {
      granted = false
      measuring = false
      pendingCalls = []
      // Not loaded on this page: nothing measures.
      const loader = window.bfLoadAppsFlyer
      if (loader && !loader.started) return
      reconcile()
    }
  }
}
